import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type InstitutionRequestStatus = "pending" | "contacted" | "approved" | "rejected";

export interface InstitutionRequest {
  id: string;
  legal_name: string;
  trade_name: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  contact_name: string;
  contact_title: string | null;
  contact_email: string;
  contact_phone: string | null;
  approx_students: number | null;
  program_offered: string | null;
  license_number: string | null;
  authorized_representative: boolean;
  language: string;
  status: InstitutionRequestStatus;
  internal_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_school_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Reads public.institution_access_requests. RLS restricts SELECT to global
 * admin / owner — no frontend-only hiding.
 */
export const useInstitutionRequests = () => {
  const [requests, setRequests] = useState<InstitutionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("institution_access_requests")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      setError(error.message);
      setRequests([]);
    } else {
      setError(null);
      setRequests((data ?? []) as unknown as InstitutionRequest[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void fetchRequests();
  }, [fetchRequests]);

  return { requests, loading, error, refetch: fetchRequests };
};

/** Lightweight pending counter for the admin nav badge. */
export const usePendingInstitutionRequests = (enabled: boolean) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const { count: pending, error } = await supabase
        .from("institution_access_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending");
      if (!cancelled && !error) setCount(pending ?? 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return count;
};
